import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import {
  PROFIL_STATUS,
  QUALIFIKATIONEN,
  ARBEITSBEREICHE,
  ARBEITSMODELLE,
  BUNDESLAENDER,
  ANERKENNUNGSSTATUS,
} from '../../lib/constants'
import { SWITCH_REASON_BY_ID, NO_GO_BY_ID } from '../../lib/matching/catalog'
import type { Pflegekraft, PflegekraftWechselgrund } from '../../lib/types'
import SliderProfile from './SliderProfile'
import './AdminPages.css'

export default function PflegekraftDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<Pflegekraft | null>(null)
  const [wechselgruende, setWechselgruende] = useState<PflegekraftWechselgrund[]>([])
  const [loading, setLoading] = useState(true)
  const [editNote, setEditNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (id) loadProfile()
  }, [id])

  async function loadProfile() {
    setLoading(true)
    const [profileRes, gruendeRes] = await Promise.all([
      supabase.from('pflegekraefte').select('*').eq('id', id).single(),
      supabase.from('pflegekraft_wechselgruende').select('*').eq('pflegekraft_id', id),
    ])
    if (profileRes.data) {
      setProfile(profileRes.data as Pflegekraft)
      setEditNote(profileRes.data.interne_notizen || '')
    }
    setWechselgruende((gruendeRes.data as PflegekraftWechselgrund[]) || [])
    setLoading(false)
  }

  async function updateStatus(newStatus: string) {
    if (!id) return
    setSaving(true)
    await supabase.from('pflegekraefte').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', id)
    setProfile((prev) => (prev ? { ...prev, status: newStatus as Pflegekraft['status'] } : null))
    setSaving(false)
  }

  async function saveNotes() {
    if (!id) return
    setSaving(true)
    await supabase.from('pflegekraefte').update({ interne_notizen: editNote, updated_at: new Date().toISOString() }).eq('id', id)
    setSaving(false)
  }

  const label = (list: readonly { value: string; label: string }[], val: string | null) =>
    list.find((i) => i.value === val)?.label || val || '–'
  const verf = (val: string | null) =>
    val === 'ja' ? '✅ Ja' : val === 'optional' ? '🟡 Nur als Option' : val === 'nein' ? '❌ Nein' : '–'
  const jaNein = (val: boolean | null) => (val === true ? '✅ Ja' : val === false ? '❌ Nein' : '–')

  if (loading) return <div className="table-loading"><div className="loading-spinner" /></div>
  if (!profile) return <div className="empty-state"><p>Profil nicht gefunden.</p></div>

  const statusColor = PROFIL_STATUS.find((s) => s.value === profile.status)?.color || '#94A3B8'
  const sortedGruende = [...wechselgruende].sort(
    (a, b) => (a.top_rang ?? 99) - (b.top_rang ?? 99) || (b.intensitaet ?? 0) - (a.intensitaet ?? 0),
  )

  return (
    <div className="admin-page animate-fade-in-up">
      <button className="back-button" onClick={() => navigate('/admin/pflegekraefte')}>← Zurück zur Liste</button>

      <div className="detail-header">
        <div className="detail-title-row">
          <h1>{profile.vorname} {profile.nachname}</h1>
          <span className="status-badge status-badge--lg" style={{ '--badge-color': statusColor } as React.CSSProperties}>
            {PROFIL_STATUS.find((s) => s.value === profile.status)?.label}
          </span>
        </div>
        <p className="page-subtitle">{profile.email} · {profile.telefon || 'Kein Telefon'}</p>
      </div>

      <div className="detail-section">
        <h3>Status ändern</h3>
        <div className="status-buttons">
          {PROFIL_STATUS.map((s) => (
            <button
              key={s.value}
              className={`status-btn ${profile.status === s.value ? 'status-btn--active' : ''}`}
              style={{ '--btn-color': s.color } as React.CSSProperties}
              onClick={() => updateStatus(s.value)}
              disabled={saving}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-section">
          <h3>👤 Grunddaten</h3>
          <dl className="detail-dl">
            <dt>PLZ / Ort</dt><dd>{profile.plz} {profile.wohnort}</dd>
            <dt>Bundesland</dt><dd>{label(BUNDESLAENDER, profile.bundesland)}</dd>
            <dt>Führerschein</dt><dd>{jaNein(profile.fuehrerschein)}</dd>
            <dt>Eigener PKW</dt><dd>{jaNein(profile.eigener_pkw)}</dd>
            <dt>Umzug möglich</dt><dd>{profile.umzug_moeglich === true ? `✅ Ja${profile.umzug_wohin ? ` — ${profile.umzug_wohin}` : ''}` : jaNein(profile.umzug_moeglich)}</dd>
            <dt>Max. Arbeitsweg</dt><dd>{profile.max_arbeitsweg_km ? `${profile.max_arbeitsweg_km} km` : '–'}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>📋 Berufliches Profil</h3>
          <dl className="detail-dl">
            <dt>Qualifikation</dt><dd>{label(QUALIFIKATIONEN, profile.qualifikation)}</dd>
            <dt>Anerkennung</dt><dd>{label(ANERKENNUNGSSTATUS, profile.anerkennungsstatus)}</dd>
            <dt>Erfahrung</dt><dd>{profile.berufserfahrung_jahre != null ? `${profile.berufserfahrung_jahre} Jahre` : '–'}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>🎯 Wechselwünsche</h3>
          <dl className="detail-dl">
            <dt>Gew. Bereiche</dt>
            <dd>{profile.gewuenschte_bereiche?.length ? profile.gewuenschte_bereiche.map((b) => label(ARBEITSBEREICHE, b)).join(', ') : '–'}</dd>
            <dt>Wochenstunden</dt><dd>{profile.gewuenschte_wochenstunden || '–'}</dd>
            <dt>Modelle</dt>
            <dd>{profile.gewuenschte_arbeitsmodelle?.length ? profile.gewuenschte_arbeitsmodelle.map((m) => label(ARBEITSMODELLE, m)).join(', ') : '–'}</dd>
            <dt>Wechselzeitpunkt</dt><dd>{profile.fruehester_wechselzeitpunkt ? new Date(profile.fruehester_wechselzeitpunkt).toLocaleDateString('de-DE') : '–'}</dd>
            <dt>Mindestgehalt</dt><dd>{profile.mindestgehalt_brutto ? `${profile.mindestgehalt_brutto.toLocaleString('de-DE')} €` : '–'}</dd>
            <dt>Wunschgehalt</dt><dd>{profile.gehaltswunsch_brutto ? `${profile.gehaltswunsch_brutto.toLocaleString('de-DE')} €` : '–'}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>🕐 Schicht-Bereitschaft</h3>
          <dl className="detail-dl">
            <dt>Nachtdienst</dt><dd>{verf(profile.nachtdienst_moeglich)}</dd>
            <dt>Wochenende</dt><dd>{verf(profile.wochenenddienst_moeglich)}</dd>
            <dt>Geteilte Dienste</dt><dd>{verf(profile.geteilte_dienste_ok)}</dd>
            <dt>Rufbereitschaft</dt><dd>{verf(profile.rufbereitschaft_ok)}</dd>
          </dl>
        </div>
      </div>

      {/* No-Gos */}
      {profile.no_gos && profile.no_gos.length > 0 && (
        <div className="detail-section">
          <h3>🛡️ Absolute No-Gos</h3>
          <div className="chip-list">
            {profile.no_gos.map((n) => (
              <span key={n} className="chip chip--red">
                {NO_GO_BY_ID[n]?.icon} {NO_GO_BY_ID[n]?.label || n}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Wechselgründe */}
      <div className="detail-section">
        <h3>💭 Wechselgründe & Intensität</h3>
        {sortedGruende.length > 0 ? (
          <div className="profile-sliders">
            {sortedGruende.map((w) => {
              const r = SWITCH_REASON_BY_ID[w.grund]
              return (
                <div key={w.id} className="profile-slider-row" style={{ fontSize: 'var(--text-sm)' }}>
                  {w.top_rang ? <span className="mb-toprang">#{w.top_rang}</span> : <span style={{ width: 24 }} />}
                  <span className="profile-slider-row-label">{r?.icon} {r?.label || w.grund}</span>
                  <span className="profile-slider-val">Intensität {w.intensitaet ?? 0}/5</span>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="empty-hint">Keine Wechselgründe angegeben.</p>
        )}
        {profile.freitext_ausschluss && (
          <div className="detail-freetext"><strong>Was nicht wieder passieren soll:</strong><p>{profile.freitext_ausschluss}</p></div>
        )}
        {profile.freitext_wichtig && (
          <div className="detail-freetext"><strong>Was am wichtigsten ist:</strong><p>{profile.freitext_wichtig}</p></div>
        )}
      </div>

      {/* Slider-Profil */}
      <div className="detail-section">
        <h3>⭐ Wunsch-Profil (Slider)</h3>
        <SliderProfile answers={profile.slider_antworten} side="candidate" />
      </div>

      {/* Wechselbegleitung */}
      <div className="detail-section">
        <h3>🧭 Wechselbegleitung</h3>
        <dl className="detail-dl">
          <dt>Hospitation</dt><dd>{profile.trial_day_interesse || '–'}</dd>
          <dt>Umzugs-Support</dt><dd>{profile.umzugs_support || '–'}</dd>
          <dt>Anerkennungs-Support</dt><dd>{profile.anerkennungs_support || '–'}</dd>
        </dl>
      </div>

      <div className="detail-section">
        <h3>📝 Interne Notizen</h3>
        <textarea
          value={editNote}
          onChange={(e) => setEditNote(e.target.value)}
          className="notes-textarea"
          rows={4}
          placeholder="Interne Notizen hinzufügen..."
        />
        <button className="btn btn--primary" onClick={saveNotes} disabled={saving}>
          {saving ? 'Speichern...' : 'Notizen speichern'}
        </button>
      </div>
    </div>
  )
}
